import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageTitle from "@/components/base/PageTitle";

const features = [
  {
    icon: "ri-user-line",
    title: "PERSONALIZED\nSOURCING",
    desc: "Every diamond is sourced based on the client's preferred specifications, ensuring a tailored and highly curated experience.",
  },
  {
    icon: "ri-award-line",
    title: "IGI-CERTIFIED\nQUALITY",
    desc: "Each selected stone is independently certified and carefully reviewed to ensure authenticity, quality, and confidence.",
  },
  {
    icon: "ri-global-line",
    title: "GLOBAL SUPPLIER\nACCESS",
    desc: "Through trusted international sourcing networks, Zayan Atelier provides access to exceptional diamonds across a wide range of specifications and styles.",
  },
];

export default function About() {
  const leftRef = useScrollReveal<HTMLDivElement>();
  const featuresRef = useScrollReveal<HTMLDivElement>();
  const ctaRef = useScrollReveal<HTMLDivElement>();

  return (
    <div className="min-h-screen bg-zayan-bg flex flex-col">
      <PageTitle title="About - Zayan Atelier" />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-[130px] md:pt-[170px] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[70vh]">
          {/* Left Content */}
          <div className="flex flex-col justify-center px-8 lg:px-16 py-16 lg:py-24 reveal" ref={leftRef}>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-zayan-gold text-xs tracking-[0.2em] font-sans">ABOUT ZAYAN ATELIER</span>
              <div className="h-px w-12 bg-zayan-gold" />
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl text-zayan-gold leading-tight mb-6">
              The Modern Diamond Sourcing Experience
            </h2>
            <div className="h-px w-16 bg-zayan-gold mb-8" />
            <div className="space-y-5 text-zayan-textMuted text-sm leading-relaxed max-w-lg">
              <p>
                Zayan Atelier was founded on the belief that exceptional diamonds should be sourced with transparency, refinement, and personalization.
              </p>
              <p>
                Through trusted international supplier relationships, we provide clients access to carefully curated IGI-certified lab-grown diamonds without the excessive overhead associated with traditional retail inventory models.
              </p>
              <p>
                Our approach focuses on tailored sourcing, personalized guidance, and refined selections designed around each client's exact preferences.
              </p>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative overflow-hidden h-64 sm:h-80 lg:h-auto">
            <img
              src="https://readdy.ai/api/search-image?query=elegant%20oval%20cut%20diamond%20engagement%20ring%20on%20dark%20black%20silk%20fabric%2C%20luxury%20fine%20jewelry%20photography%2C%20gold%20prong%20setting%2C%20brilliant%20sparkle%2C%20dramatic%20studio%20lighting%2C%20ultra%20detailed%2C%20high%20contrast&width=700&height=700&seq=about-hero&orientation=portrait"
              alt="Diamond ring on silk"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zayan-bg/60 to-transparent" />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="border-t border-zayan-border">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zayan-border reveal" ref={featuresRef}>
          {features.map((f, i) => (
            <div key={i} className={`px-8 lg:px-12 py-12 flex flex-col gap-6 hover:bg-zayan-gold/5 transition-colors duration-500`}>
              <div className="w-14 h-14 flex items-center justify-center rounded-full border border-zayan-gold/50">
                <i className={`${f.icon} text-zayan-gold text-xl`} />
              </div>
              <div>
                <h3 className="text-zayan-gold text-sm tracking-[0.15em] font-sans mb-1 whitespace-pre-line">
                  {f.title}
                </h3>
                <div className="h-px w-8 bg-zayan-gold/50 mb-4" />
                <p className="text-zayan-textMuted text-sm leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="bg-zayan-bgCard border-t border-zayan-border py-16 px-6 text-center reveal" ref={ctaRef}>
        <div className="w-14 h-14 flex items-center justify-center rounded-full border border-zayan-gold/50 mx-auto mb-6">
          <i className="ri-vip-diamond-line text-zayan-gold text-xl" />
        </div>
        <h2 className="font-serif text-3xl lg:text-5xl text-zayan-gold mb-4">
          Modern diamond sourcing, thoughtfully refined.
        </h2>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-zayan-textDarker text-xs tracking-[0.2em]">
          <span>REFINED</span>
          <span className="text-zayan-gold">•</span>
          <span>RARE</span>
          <span className="text-zayan-gold">•</span>
          <span>RADIANT</span>
        </div>
      </section>

      <Footer />
    </div>
  );
}